import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useFunnelEditor - Hook for managing funnel editor state
 *
 * Handles:
 * - Loading funnel data from API
 * - Tracking changes (dirty state)
 * - Debounced auto-save
 * - Undo/redo history
 * - Publishing
 */
export default function useFunnelEditor(funnelId, pageId) {
  const [funnel, setFunnel] = useState(null);
  const [page, setPage] = useState(null);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Undo/redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistorySize = 50;

  // Debounce timer
  const saveTimer = useRef(null);
  const autoSaveDelay = 2000; // 2 seconds

  // API base URL (adjust for your environment)
  const API_BASE = '/api';

  // Load funnel data
  const loadFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load funnel with tenant branding
      const funnelRes = await fetch(`${API_BASE}/funnels/${funnelId}`);
      if (!funnelRes.ok) throw new Error('Failed to load funnel');
      const funnelData = await funnelRes.json();

      // Load specific page
      const pageRes = await fetch(`${API_BASE}/funnels/pages/${pageId}`);
      if (!pageRes.ok) throw new Error('Failed to load page');
      const pageData = await pageRes.json();

      setFunnel(funnelData.funnel);
      setPage(pageData.page);
      setBranding(funnelData.funnel?.branding || {});

      // Initialize history
      setHistory([{ page: pageData.page, branding: funnelData.funnel?.branding || {} }]);
      setHistoryIndex(0);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load funnel:', err);
    } finally {
      setLoading(false);
    }
  }, [funnelId, pageId]);

  // Initial load
  useEffect(() => {
    if (funnelId && pageId) {
      loadFunnel();
    }
  }, [funnelId, pageId, loadFunnel]);

  // Add to history (for undo/redo)
  const addToHistory = useCallback((newPage, newBranding) => {
    setHistory((prev) => {
      // Remove any future history if we're not at the end
      const truncated = prev.slice(0, historyIndex + 1);

      // Add new state
      const updated = [...truncated, { page: newPage, branding: newBranding }];

      // Limit history size
      if (updated.length > maxHistorySize) {
        return updated.slice(-maxHistorySize);
      }

      return updated;
    });

    setHistoryIndex((prev) => Math.min(prev + 1, maxHistorySize - 1));
  }, [historyIndex]);

  // Update page content
  const updatePage = useCallback((updates) => {
    setPage((prev) => {
      const newPage = {
        ...prev,
        content: {
          ...prev.content,
          ...updates
        }
      };

      addToHistory(newPage, branding);
      return newPage;
    });

    setHasChanges(true);
  }, [branding, addToHistory]);

  // Update section content
  const updateSection = useCallback((sectionIndex, updates) => {
    setPage((prev) => {
      const newSections = [...(prev.content?.sections || [])];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        content: {
          ...(newSections[sectionIndex]?.content || {}),
          ...updates
        }
      };

      const newPage = {
        ...prev,
        content: {
          ...prev.content,
          sections: newSections
        }
      };

      addToHistory(newPage, branding);
      return newPage;
    });

    setHasChanges(true);
  }, [branding, addToHistory]);

  // Update branding
  const updateBranding = useCallback((updates) => {
    setBranding((prev) => {
      const newBranding = { ...prev, ...updates };
      addToHistory(page, newBranding);
      return newBranding;
    });

    setHasChanges(true);
  }, [page, addToHistory]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = history[newIndex];

      setPage(state.page);
      setBranding(state.branding);
      setHistoryIndex(newIndex);
      setHasChanges(true);
    }
  }, [historyIndex, history]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const state = history[newIndex];

      setPage(state.page);
      setBranding(state.branding);
      setHistoryIndex(newIndex);
      setHasChanges(true);
    }
  }, [historyIndex, history]);

  // Save to API
  const save = useCallback(async () => {
    if (!hasChanges || saving) return;

    setSaving(true);
    setError(null);

    try {
      // Save page content
      const pageRes = await fetch(`${API_BASE}/funnels/pages/${pageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: page.content,
          title: page.title
        })
      });

      if (!pageRes.ok) throw new Error('Failed to save page');

      // Save branding to funnel
      const brandingRes = await fetch(`${API_BASE}/funnels/${funnelId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          branding: branding
        })
      });

      if (!brandingRes.ok) throw new Error('Failed to save branding');

      setHasChanges(false);
      setLastSaved(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  }, [hasChanges, saving, page, branding, pageId, funnelId]);

  // Auto-save with debounce
  useEffect(() => {
    if (hasChanges) {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }

      saveTimer.current = setTimeout(() => {
        save();
      }, autoSaveDelay);
    }

    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    };
  }, [hasChanges, save]);

  // Publish funnel
  const publish = useCallback(async () => {
    // Save first
    await save();

    try {
      const res = await fetch(`${API_BASE}/funnels/${funnelId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) throw new Error('Failed to publish');

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [funnelId, save]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        save();
      }

      // Ctrl/Cmd + Z to undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y to redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [save, undo, redo]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  return {
    // Data
    funnel,
    page,
    branding,

    // State
    loading,
    saving,
    error,
    hasChanges,
    lastSaved,

    // History
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,

    // Actions
    updatePage,
    updateSection,
    updateBranding,
    save,
    publish,
    undo,
    redo,
    reload: loadFunnel
  };
}

/**
 * useDebouncedCallback - Helper for debouncing callbacks
 */
export function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}
